import {
  ACTION_SUMMARY_COUNT_PII_MAILS,
  fetchPiiEmailsCount
} from '../../../dashboardSummary/dashboardSummarySlice'
import {
  fetchGMailSummaryData,
  fetchOutlookSummaryData
} from '../../../../features/summary/summarySlice'

import DashboardSummaryCountsWidget from '../../../../components/DashboardSummaryCountsWidget/SummaryCountWidget'
import SvgIconPolicies from '../../../../assets/inlineSvg/icon-policies.svg'
import SvgIconMembers from '../../../../assets/inlineSvg/icon-members.svg'
import SvgIconArrowsUpDown from '../../../../assets/inlineSvg/icon-arrows-up-down.svg'
import SvgIconEmail from '../../../../assets/inlineSvg/icon-mail.svg'
import { RootState } from '../../../../rootReducer'
import {
  GMailSummary,
  GMailSummaryQueryParams,
  OutlookSummary,
  OutlookSummaryQueryParams
} from '../../../../services/api/apiTypes'
import useUrlContext from '../../../../hooks/useUrlContext'
import { IGetDatasourcePoliciesCountParams } from '../../../dashboardSummary/queries'
import {
  DATA_SOURCE_TYPE,
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPES,
  FEATURE_FLAGS,
  URL_EMAILS
} from '../../../../constants'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import { getShowLoader } from '../../../../reducers/requestReducer'
import { FeatureFlags } from '../../../../configs/featureFlagSlice'
import { mockMailsSummaryCountWidgetData } from '../../renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../../mainDashboardv2/constants'
import { getDataSourceBasePath } from '../../../../utils/dataSourceUtil'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import React, { useEffect } from 'react'
import { Typography } from '@lightbeamai/design-system'
import { Popup } from 'semantic-ui-react'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
  widgetDashboardId?: string
}

interface StateProps {
  gmailData: GMailSummary
  outLookData: OutlookSummary
  policiesCount?: number
  piiEmailsCount?: number
  inboxEmailCount?: number
  sentEmailsCount?: number
  showLoader?: boolean
  featureFlags: FeatureFlags
}

interface DispatchProps {
  fetchPiiEmailsCount: (params: IGetDatasourcePoliciesCountParams) => void
  fetchGMailSummaryData: (
    params: GMailSummaryQueryParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
  fetchOutlookSummaryData: (
    params: OutlookSummaryQueryParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
}

type MailsSummaryCountComponentProps = OwnProps & StateProps & DispatchProps

const MailsSummaryCountsWidget = ({
  gmailData: { usersCount: gmailUsersCount },
  outLookData: { usersCount: outLookUsersCount },
  fetchPiiEmailsCount,
  piiEmailsCount = 0,
  inboxEmailCount = 0,
  sentEmailsCount = 0,
  policiesCount,
  showLoader,
  featureFlags,
  useMockData = false,
  widgetDatasourceId,
  widgetDatasourceType,
  widgetDashboardId,
  fetchGMailSummaryData,
  fetchOutlookSummaryData
}: MailsSummaryCountComponentProps) => {
  const context = useUrlContext()
  const intl = useIntl()
  const datasourceId = widgetDatasourceId ? widgetDatasourceId : context.contextFilters.datasourceId
  const dataSourceType = widgetDatasourceType
    ? widgetDatasourceType
    : context.queryParams.datasourceType

  const isGmailDatasource = dataSourceType === DATA_SOURCE_TYPES.gmail
  const isOutlookDatasource = dataSourceType === DATA_SOURCE_TYPES.outLook

  const usersCount = isGmailDatasource ? gmailUsersCount : outLookUsersCount

  const isInboxScanningEnabled = !!(
    featureFlags?.[FEATURE_FLAGS.INBOX_SCANNING] && isOutlookDatasource
  )
  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  useEffect(() => {
    if (useMockData) return
    if (datasourceId) {
      const params = {
        datasourceId,
        [UNIQUE_WIDGET_INDEX]: widgetIndex
      }
      fetchPiiEmailsCount(params)
    }
  }, [datasourceId, fetchPiiEmailsCount, useMockData])

  useEffect(() => {
    if (useMockData) return
    if (datasourceId) {
      const params = {
        datasourceId,
        [UNIQUE_WIDGET_INDEX]: widgetIndex
      }

      if (isGmailDatasource) {
        fetchGMailSummaryData(params)
      } else {
        fetchOutlookSummaryData(params)
      }
    }
  }, [datasourceId, isGmailDatasource, isOutlookDatasource, useMockData, widgetIndex])

  const pathArr = getDataSourceBasePath(
    widgetDatasourceType,
    datasourceId,
    context.pathname,
    true
  ) as string[]
  const baseUrl = pathArr.join('/')
  const widgetData = [
    {
      [isInboxScanningEnabled ? 'content' : 'count']: isInboxScanningEnabled ? (
        <div>
          <Popup
            className="summary-tooltip-popup"
            trigger={
              <span className="summary-tooltip">
                <Typography color="secondary" as="span" type="strong" variant="h4">
                  {intl.formatNumber(sentEmailsCount + inboxEmailCount, { notation: 'compact' })}
                </Typography>
                <SvgIconArrowsUpDown
                  size={14}
                  color="var(--color-alerts-action-text)"
                  className="xs-ml-4"
                  weight="bold"
                />
              </span>
            }
            content={
              <div className="emails-counts-tooltip">
                <div className="emails-counts-tooltip-item">
                  <Typography variant="baseMinus2">
                    {intl.formatMessage({ id: 'mail.widget.tooltip.sentMails' })}
                  </Typography>
                  <Typography variant="baseMinus2">{intl.formatNumber(sentEmailsCount)}</Typography>
                </div>
                <div className="emails-counts-tooltip-item">
                  <Typography variant="baseMinus2">
                    {intl.formatMessage({ id: 'mail.widget.tooltip.receivedMails' })}
                  </Typography>
                  <Typography variant="baseMinus2">{intl.formatNumber(inboxEmailCount)}</Typography>
                </div>
              </div>
            }
            position="top center"
            size="tiny"
            inverted
          />
        </div>
      ) : (
        piiEmailsCount
      ),
      icon: <SvgIconEmail />,
      text: intl.formatMessage({ id: 'dashboard.mail.widget.count.piiEmails' }),
      link: getUpdatedUrl({
        pathName: baseUrl + URL_EMAILS,
        pageName: URL_EMAILS,
        queryParams: {
          ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
          ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
        }
      })
    },
    {
      icon: <SvgIconPolicies />,
      text: intl.formatMessage({ id: 'dashboard.dataSource.widget.text.policies' }),
      count: policiesCount || 0
    },
    {
      icon: <SvgIconMembers />,
      text: intl.formatMessage({ id: 'dashboard.dataSource.widget.text.members' }),
      count: usersCount || 0
    }
  ]

  return (
    <DashboardSummaryCountsWidget
      summaryItems={widgetData}
      showLoader={showLoader}
      useMockData={useMockData}
    />
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)

  return {
    gmailData: ownProps?.useMockData
      ? mockMailsSummaryCountWidgetData.gmailData
      : state.summary.widgetGmailSummaryData?.[widgetIndex] || {},
    outLookData: ownProps?.useMockData
      ? mockMailsSummaryCountWidgetData.outLookData
      : state.summary.widgetOutlookSummaryData?.[widgetIndex] || {},
    policiesCount: ownProps?.useMockData
      ? mockMailsSummaryCountWidgetData.policiesCount
      : state.dashboardSummaryCounts.policiesCount?.[widgetIndex],
    piiEmailsCount: ownProps?.useMockData
      ? mockMailsSummaryCountWidgetData.piiEmailsCount
      : state.dashboardSummaryCounts.piiEmailsCount?.[widgetIndex],
    inboxEmailCount: ownProps?.useMockData
      ? mockMailsSummaryCountWidgetData.inboxEmailCount
      : state.dashboardSummaryCounts.inboxEmailCount?.[widgetIndex],
    sentEmailsCount: ownProps?.useMockData
      ? mockMailsSummaryCountWidgetData.sentEmailsCount
      : state.dashboardSummaryCounts.sentEmailsCount?.[widgetIndex],
    featureFlags: state.featureFlags,
    showLoader: getShowLoader(ACTION_SUMMARY_COUNT_PII_MAILS)
  }
}
const mapDispatchToProps: DispatchProps = {
  fetchPiiEmailsCount,
  fetchGMailSummaryData,
  fetchOutlookSummaryData
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(MailsSummaryCountsWidget)
